import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Repeat, Sparkles, Coins, ChevronRight } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { toast } from "sonner";

const RARITIES = [
  { key: "", label: "Any rarity" },
  { key: "common", label: "Common" },
  { key: "uncommon", label: "Uncommon" },
  { key: "rare", label: "Rare" },
  { key: "epic", label: "Epic" },
  { key: "legendary", label: "Legendary" },
  { key: "goddess", label: "Goddess" },
];

/**
 * Compact "quick request" form that lives inside the Trade chat channel.
 * Lets a player post a Give/Want request to the Request Board without
 * leaving the chat. Supports "any rarity" wildcards on both sides so a
 * player can literally say "give any epic → want any legendary" or
 * "offering 500 credits — want any goddess-tier card" in one action.
 *
 * On submit, we:
 *   1. POST /api/request-board with the parsed body.
 *   2. POST a chat message into the current channel with a link to the
 *      new request so anyone in the room can jump straight to it.
 */
export function TradeQuickRequest({ channel, open, onOpenChange, onPosted }) {
  const qc = useQueryClient();
  const [wantRarity, setWantRarity] = useState("");
  const [wantTags, setWantTags] = useState("");
  const [wantCredits, setWantCredits] = useState("");
  const [offerCredits, setOfferCredits] = useState("");
  const [note, setNote] = useState("");
  const [openBounty, setOpenBounty] = useState(false);      // "Give any" — no bounty attached yet
  const [openWant, setOpenWant] = useState(false);          // "Want any" — accepts any card / offer

  const reset = () => {
    setWantRarity(""); setWantTags(""); setWantCredits("");
    setOfferCredits(""); setNote("");
    setOpenBounty(false); setOpenWant(false);
  };

  const post = useMutation({
    mutationFn: async () => {
      const tags = wantTags
        .split(",")
        .map((s) => s.trim().toLowerCase())
        .filter(Boolean)
        .slice(0, 8);
      const wantCr = Math.max(0, parseInt(wantCredits || "0", 10) || 0);
      const offerCr = Math.max(0, parseInt(offerCredits || "0", 10) || 0);

      // Build a natural-language title so the Request Board row is scannable.
      const wantParts = [];
      if (openWant) wantParts.push("Any card");
      else if (wantRarity) wantParts.push(`Any ${wantRarity}`);
      if (tags.length) wantParts.push(`(${tags.join(", ")})`);
      if (wantCr > 0) wantParts.push(`+ ${wantCr.toLocaleString()} credits`);
      const wantText = wantParts.length ? wantParts.join(" ") : "Anything reasonable";

      const offerParts = [];
      if (openBounty) offerParts.push("Open to offers");
      if (offerCr > 0) offerParts.push(`${offerCr.toLocaleString()} credits`);
      const offerText = offerParts.length ? offerParts.join(" · ") : "Open to offers";

      const title = `WANT: ${wantText}`.slice(0, 118);
      const description = `Give: ${offerText}${note.trim() ? `\n\n${note.trim()}` : ""}`.slice(0, 990);

      // "Want" side — at least one non-empty want is required by backend.
      // We fall back to a nominal 1-credit want if the poster only set
      // rarity/tags (both are "hints" but not on their own bounty-worthy),
      // so the request always validates. Users can obviously accept any
      // offer on the board — this is just the tautological floor.
      const body = {
        title,
        description,
        tags,
        want_rarity: openWant ? null : (wantRarity || null),
        want_credits: wantCr > 0 ? wantCr : (wantRarity || openWant || tags.length ? 0 : 1),
        offer_credits: offerCr,
      };
      if (!openWant && !wantRarity && tags.length === 0 && wantCr === 0) {
        body.want_credits = 1;
      }
      const r = await api.post("/request-board", body);
      // Best-effort echo into the chat with a link back to the Request Board.
      try {
        await api.post(`/chat/${encodeURIComponent(channel)}`, {
          text: `📢 New trade request: ${title} — /requests`,
        });
      } catch {
        /* chat echo is best-effort; the request itself is what matters */
      }
      return r.data;
    },
    onSuccess: () => {
      toast.success("Trade request posted to the board.");
      qc.invalidateQueries({ queryKey: ["request-board"] });
      qc.invalidateQueries({ queryKey: ["chat", channel] });
      reset();
      onOpenChange?.(false);
      onPosted?.();
    },
    onError: (e) => toast.error(apiError(e, "Couldn't post the request")),
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md bg-[hsl(222_22%_7%)] border-white/10" data-testid="trade-quick-request-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-white">
            <Repeat className="h-4 w-4 text-cyan-300" />
            Post a Trade Request
          </DialogTitle>
          <DialogDescription className="text-white/55 text-xs">
            Land your ask on the Request Board without leaving chat — pick a specific rarity or open it up with &quot;any&quot;.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 text-sm">
          {/* WANT block */}
          <div className="rounded-xl border border-cyan-400/25 bg-cyan-400/[0.05] p-3">
            <div className="flex items-center gap-2 mb-2">
              <Sparkles className="h-3.5 w-3.5 text-cyan-300" />
              <span className="font-mono text-[10px] uppercase tracking-[0.28em] text-cyan-300">You want</span>
              <label className="ml-auto inline-flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-[0.22em] text-white/60">
                <input
                  type="checkbox"
                  data-testid="trade-quick-want-any"
                  checked={openWant}
                  onChange={(e) => setOpenWant(e.target.checked)}
                  className="accent-cyan-400"
                />
                Want any card
              </label>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label className="text-[10px] font-mono uppercase tracking-[0.22em] text-white/55">Rarity</Label>
                <select
                  data-testid="trade-quick-want-rarity"
                  disabled={openWant}
                  value={wantRarity}
                  onChange={(e) => setWantRarity(e.target.value)}
                  className="mt-1 w-full h-9 rounded-md border border-white/10 bg-white/5 text-white text-sm px-2 disabled:opacity-40"
                >
                  {RARITIES.map((r) => (<option key={r.key} value={r.key}>{r.label}</option>))}
                </select>
              </div>
              <div>
                <Label className="text-[10px] font-mono uppercase tracking-[0.22em] text-white/55">+ Credits (opt.)</Label>
                <Input
                  data-testid="trade-quick-want-credits"
                  type="number"
                  min={0}
                  step={50}
                  placeholder="0"
                  value={wantCredits}
                  onChange={(e) => setWantCredits(e.target.value)}
                  className="mt-1 h-9 bg-white/5 border-white/10 text-white"
                />
              </div>
            </div>
            <div className="mt-2">
              <Label className="text-[10px] font-mono uppercase tracking-[0.22em] text-white/55">Tag hints (optional, comma-separated)</Label>
              <Input
                data-testid="trade-quick-want-tags"
                placeholder="e.g. dragon, cyber, forge"
                value={wantTags}
                onChange={(e) => setWantTags(e.target.value)}
                className="mt-1 h-9 bg-white/5 border-white/10 text-white"
              />
            </div>
          </div>

          {/* OFFER block */}
          <div className="rounded-xl border border-fuchsia-400/25 bg-fuchsia-400/[0.05] p-3">
            <div className="flex items-center gap-2 mb-2">
              <Coins className="h-3.5 w-3.5 text-amber-300" />
              <span className="font-mono text-[10px] uppercase tracking-[0.28em] text-fuchsia-300">You give</span>
              <label className="ml-auto inline-flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-[0.22em] text-white/60">
                <input
                  type="checkbox"
                  data-testid="trade-quick-give-any"
                  checked={openBounty}
                  onChange={(e) => setOpenBounty(e.target.checked)}
                  className="accent-fuchsia-400"
                />
                Open to offers
              </label>
            </div>
            <div>
              <Label className="text-[10px] font-mono uppercase tracking-[0.22em] text-white/55">Credits offered (opt.)</Label>
              <Input
                data-testid="trade-quick-offer-credits"
                type="number"
                min={0}
                step={50}
                placeholder="0"
                value={offerCredits}
                onChange={(e) => setOfferCredits(e.target.value)}
                className="mt-1 h-9 bg-white/5 border-white/10 text-white"
              />
              <p className="mt-1.5 text-[11px] text-white/40 leading-snug">
                To bundle cards or items with your offer, use the full{" "}
                <a href="/requests" className="text-fuchsia-300 hover:text-fuchsia-200 underline underline-offset-2">Request Board</a>.
              </p>
            </div>
          </div>

          <div>
            <Label className="text-[10px] font-mono uppercase tracking-[0.22em] text-white/55">Note</Label>
            <Textarea
              data-testid="trade-quick-note"
              placeholder="Anything else other traders should know…"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              maxLength={500}
              className="mt-1 bg-white/5 border-white/10 text-white min-h-[60px]"
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange?.(false)} data-testid="trade-quick-cancel" className="text-white/70">
            Cancel
          </Button>
          <Button
            onClick={() => post.mutate()}
            disabled={post.isPending}
            data-testid="trade-quick-submit"
            className="bg-cyan-400 text-black hover:bg-cyan-300"
          >
            {post.isPending ? "Posting…" : (<>Post request <ChevronRight className="h-4 w-4 ml-0.5" /></>)}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
